// Route: /catalog/$slug — ПУБЛИЧНАЯ витрина курса.
//
// Не вложен в `/catalog` (имя файла с `_`): каталог рисует свой контент и не
// держит `<Outlet/>`, а вложенный child без него молча не отрисовался бы — URL
// сменился, экран прежний (`rules/code-quality.md` §35).
//
// Роут — КАРКАС: загрузка, пять состояний, мета и микроразметка. Композицию
// секций рисует `CourseLanding` (`components/courses/public/course-landing`),
// одна и та же вёрстка для публичной страницы и для той же карточки внутри
// сервиса (§3.1 плана): второго «промо-шаблона» не существует.
//
// Один запрос на страницу: `getPublic` отдаёт описание, программу, автора,
// цену, статистику и похожие курсы разом (§7.2). Секции за данными не ходят —
// иначе открытие стоило бы пяти round-trip'ов, а половина блоков мигала бы
// скелетонами вразнобой.
//
// Пять состояний §10A: loading (скелетон раскладки) · offline (витрина живёт
// только онлайн, локальной копии нет) · error (`ErrorState` + retry, отдельный
// текст для несуществующего курса) · data · пустого состояния у экрана нет:
// курс либо есть, либо это ошибка.
//
// Хлебных крошек в интерфейсе НЕТ намеренно: `Breadcrumb` в проекте не
// используется ни разу, канон «назад» — ghost-кнопка со стрелкой. Крошки
// отдаются только машине, в `BreadcrumbList` JSON-LD.

import type { CoursePublicDetail } from "@studyhub/shared/contracts/course-templates";
import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button } from "@studyhub/ui/components/button";
import { ErrorState } from "@studyhub/ui/components/error-state";
import { IconArrowLeft, IconPlayerPlay } from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useCoursePublic } from "@/components/courses/catalog/_api";
import { PriceBadge } from "@/components/courses/price-badge";
import { ENROLL_CTA_LABEL_KEY, resolveEnrollState } from "@/components/courses/public/_types";
import {
	type EnrollOutcome,
	resolveStartIntent,
	useEnrollCourse,
} from "@/components/courses/public/_use-start-course";
import { CourseLanding } from "@/components/courses/public/course-landing";
import {
	PaidCourseDialog,
	type PaymentRefusal,
	resolvePaymentRefusal,
} from "@/components/courses/public/paid-course-dialog";
import {
	StartCourseDialog,
	toastEnrollOutcome,
} from "@/components/courses/public/start-course-dialog";
import { JsonLd } from "@/components/seo/JsonLd";
import { PageMeta } from "@/components/seo/PageMeta";
import { LIST_PAGE_SHELL } from "@/components/shared/entity-list";
import { MobileActionBar } from "@/components/shared/mobile-action-bar";
import { OfflineDetailState } from "@/components/shared/offline-detail-state";
import { GeneratePlanDialog } from "@/components/study-plan/generate-dialog/generate-plan-dialog";
import { useAddressLocale } from "@/hooks/use-address-locale";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { useRequireOnline } from "@/hooks/use-require-online";
import { useSession } from "@/hooks/use-session";
import { withLocalePrefix } from "@/lib/locale-url";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { track } from "@/lib/observability/analytics";
import { toastError } from "@/lib/user-facing-error";
import { useLoginModalStore } from "@/stores/login-modal";

const SITE_URL = "https://study.iec.exchange";

/**
 * `?start=1` — ОДНОРАЗОВЫЙ маркер «после входа сразу открыть старт курса».
 *
 * Одноразовость обязательна: параметр, оставшийся в адресе, превращает каждую
 * перезагрузку и каждый возврат по истории в самопроизвольно всплывающую
 * модалку. Поэтому обработчик снимает его через `replace: true` в тот же кадр,
 * в котором открывает диалог, — но только у авторизованного: у гостя параметр
 * ещё ждёт возврата из входа и трогать его нельзя.
 */
const searchSchema = z.object({
	/**
	 * Принимаем и строку, и число — и это не перестраховка.
	 *
	 * Роутер разбирает search через `JSON.parse` на каждое значение, поэтому
	 * `?start=1` приезжает ЧИСЛОМ 1, а не строкой «1». Схема ждала только строку
	 * и роняла страницу целиком: гость жал «Начать курс», уходил в модалку
	 * входа, возвращался по сохранённому адресу `/catalog/{slug}?start=1` — и
	 * вместо курса получал «Что-то пошло не так» с текстом Zod-ошибки (инцидент
	 * 2026-08-16). Нормализуем к строке, чтобы потребители ниже не гадали о типе.
	 */
	start: z
		.union([z.literal("1"), z.literal(1)])
		.optional()
		.transform((v) => (v === undefined ? undefined : ("1" as const))),
});

export const Route = createFileRoute("/catalog_/$slug")({
	validateSearch: tolerantSearch(searchSchema),
	component: CoursePublicPage,
	pendingComponent: CoursePublicPending,
});

/**
 * Скелетон повторяет раскладку витрины: шапка с обложкой, две колонки
 * (контент + карточка действия), полосы секций. Серый прямоугольник вместо
 * этого дал бы скачок вёрстки при подстановке данных.
 */
function CoursePublicSkeleton(): React.ReactElement {
	return (
		<div className="space-y-section" aria-busy="true">
			<div className="h-40 animate-pulse rounded-[var(--radius-card)] border border-border bg-card shadow-e1 lg:h-56" />
			<div className="flex flex-col gap-grid lg:flex-row">
				<div className="min-w-0 flex-1 space-y-3">
					{[0, 1, 2].map((index) => (
						<div
							key={index}
							className="h-28 animate-pulse rounded-[var(--radius-card)] border border-border bg-card shadow-e1"
						/>
					))}
				</div>
				<div className="h-64 w-full shrink-0 animate-pulse rounded-[var(--radius-card)] border border-border bg-card shadow-e1 lg:w-rail" />
			</div>
		</div>
	);
}

function CoursePublicPending(): React.ReactElement {
	return (
		<div className={LIST_PAGE_SHELL}>
			<CoursePublicSkeleton />
		</div>
	);
}

function CoursePublicPage(): React.ReactElement {
	const { t } = useTranslation("courses");
	const { slug } = Route.useParams();
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const online = useOnlineStatus();
	const { isAuthenticated } = useSession();
	// Запись — серверное действие; офлайн честнее отказать сразу.
	const { guard } = useRequireOnline();
	const query = useCoursePublic(slug);
	const commerceVisible = useCommerceVisible();
	// Платный курс там, где продаж не видно (приложение из App Store, guideline
	// 3.1.1), для того, кто его ещё не проходит, выглядит несуществующим: купить
	// его в приложении нельзя, а витрина с ценой — ровно то, за что заворачивали
	// сборку. Уже записанный человек курс видит — это его учёба, а не покупка.
	const hiddenFromSale =
		!commerceVisible &&
		query.data !== undefined &&
		query.data.pricing.kind === "paid" &&
		query.data.enrolledCourseId === null;
	const course = hiddenFromSale ? null : (query.data ?? null);

	const [startOpen, setStartOpen] = useState(false);
	// Программа платформы собирается диалогом генерации. Он открывается ПРЯМО
	// здесь, а не возвратом в каталог с `?preset=`: каталог ищет программу среди
	// загруженных карточек первой страницы, и человек, пришедший на страницу по
	// ссылке из поиска, оказывался бы в списке вообще без диалога.
	const [presetOpen, setPresetOpen] = useState(false);
	const [refusal, setRefusal] = useState<PaymentRefusal | null>(null);
	// Проверка оплаты — тот же `enroll`: у оплатившего курс он откроется, всем
	// остальным сервер вернёт точную причину отказа (см. `paid-course-dialog`).
	const { enroll, isPending: isProbing } = useEnrollCourse();

	// «Нет такого курса» и «не смогли загрузить» — разные сообщения: первое
	// окончательно, второе лечится повтором.
	const notFound = hiddenFromSale || query.error?.data?.code === "NOT_FOUND";

	const openCourse = useCallback(
		(courseId: string) => {
			void navigate({
				to: "/courses/$courseId",
				params: { courseId },
			});
		},
		[navigate],
	);

	const handleEnrolled = useCallback(
		(outcome: EnrollOutcome) => {
			toastEnrollOutcome(outcome, (key) => t(key, { ns: "course-public" }));
			openCourse(outcome.courseId);
		},
		[openCourse, t],
	);

	const handleStart = useCallback(() => {
		if (course === null) return;
		const intent = resolveStartIntent({ isAuthenticated, course });

		// Ступень воронки. Эмитим ДО разбора веток и ровно один раз на клик:
		// главное действие витрины ведёт в пять разных мест, и без `intent`
		// метрика склеила бы «пошёл учиться» с «его отправили логиниться».
		track("course_start_clicked", { slug, intent });

		if (intent === "login") {
			// Возврат ведёт на ту же витрину с маркером «сразу начать».
			useLoginModalStore.getState().open(`/catalog/${slug}?start=1`);
			return;
		}
		if (intent === "open-course" && course.enrolledCourseId !== null) {
			openCourse(course.enrolledCourseId);
			return;
		}
		if (intent === "import") {
			// Готовая программа платформы — тот же диалог настройки, что у курса
			// автора (решение владельца 2026-08-19). Раньше эта ветка переносила
			// программу молча, и человек попадал прямо во входной тест, ни разу не
			// сказав, с какого уровня начинать и кому объясняем.
			//
			// Перенос при этом остаётся переносом: диалог зовёт
			// `studyPlan.importTemplate`, а не генерацию — она сочинила бы свою
			// структуру вместо написанной (инцидент 2026-08-16).
			if (!guard()) return;
			setStartOpen(true);
			return;
		}
		if (intent === "preset") {
			// Программа платформы БЕЗ структуры (пресет свободной темы): переносить
			// нечего, курс собирается генерацией под даты ученика.
			if (!guard()) return;
			setPresetOpen(true);
			return;
		}
		if (!guard()) return;

		if (intent === "configure") {
			setStartOpen(true);
			return;
		}

		// `purchase`: спрашиваем сервер, а не гадаем на клиенте.
		void enroll({ templateId: course.id, pace: { targetDate: null, load: null } })
			.then(handleEnrolled)
			.catch((error: unknown) => {
				const reason = resolvePaymentRefusal(error);
				if (reason === null) {
					toastError(error, t("catalog.card.startError"), t);
					return;
				}
				setRefusal(reason);
			});
	}, [course, isAuthenticated, slug, openCourse, navigate, guard, enroll, handleEnrolled, t]);

	// Вершина воронки витрины. Считаем не рендеры, а ФАКТ показа карточки:
	// ref гасит повторные срабатывания эффекта (refetch, смена сессии,
	// возврат из диалога), иначе один визит даёт несколько «просмотров» и
	// конверсия в «Начать» занижается на ровном месте.
	const viewTracked = useRef<string | null>(null);
	useEffect(() => {
		if (course === null || viewTracked.current === course.slug) return;
		viewTracked.current = course.slug;
		track("course_public_page_viewed", {
			slug: course.slug,
			origin: course.origin,
			pricingKind: course.pricing.kind,
			authenticated: isAuthenticated,
		});
	}, [course, isAuthenticated]);

	// `?start=1` срабатывает ровно один раз за жизнь страницы: ref не даёт
	// эффекту переоткрыть диалог, пока `navigate` ещё не убрал параметр.
	const startConsumed = useRef(false);
	useEffect(() => {
		if (search.start !== "1" || !isAuthenticated || course === null) return;
		if (startConsumed.current) return;
		startConsumed.current = true;
		void navigate({ search: (prev) => ({ ...prev, start: undefined }), replace: true });
		handleStart();
	}, [search.start, isAuthenticated, course, navigate, handleStart]);

	return (
		<div className={LIST_PAGE_SHELL}>
			<div className="flex items-center gap-2">
				<Button asChild variant="ghost" size="sm" className="-ml-2 cursor-pointer">
					<Link to="/catalog" preload="viewport">
						<IconArrowLeft size={14} />
						{t("catalog.title")}
					</Link>
				</Button>
			</div>

			{course ? <CoursePublicMeta course={course} /> : null}

			<CoursePublicBody
				online={online}
				isLoading={query.isLoading}
				isError={query.isError}
				notFound={notFound}
				course={course}
				onRetry={() => void query.refetch()}
				onStart={handleStart}
				isStarting={isProbing}
			/>

			{course ? (
				<>
					{/* Запас под фиксированную панель: без него нижний блок страницы
					    оказывается под ней и до него нельзя дочитать. */}
					<div className="h-24 lg:hidden" aria-hidden />
					<CourseMobileBar course={course} onStart={handleStart} isStarting={isProbing} />

					<StartCourseDialog
						open={startOpen}
						onOpenChange={setStartOpen}
						course={course}
						// Происхождение решает, какой процедурой стартовать: подписка на
						// версию автора или перенос программы платформы. Форма настройки
						// у обеих одна.
						origin={course.origin === "official" ? "official" : "author"}
						onEnrolled={handleEnrolled}
					/>
					<GeneratePlanDialog
						open={presetOpen}
						onOpenChange={setPresetOpen}
						prefilledTemplate={{
							id: course.id,
							title: course.title,
							goal: course.listing.summary ?? course.description ?? course.title,
						}}
					/>
					{refusal !== null ? (
						<PaidCourseDialog
							open
							onOpenChange={(next) => {
								if (!next) setRefusal(null);
							}}
							refusal={refusal}
							courseTitle={course.title}
							pricing={course.pricing}
						/>
					) : null}
				</>
			) : null}
		</div>
	);
}

/**
 * Нижняя панель действия на мобильной оболочке: цена слева, главная кнопка
 * справа.
 *
 * Подпись и доступность кнопки берутся из того же `resolveEnrollState`, что и
 * карточка записи: панель и карточка — две кнопки одного действия, и разошедшиеся
 * подписи читались бы как два разных предложения.
 */
function CourseMobileBar({
	course,
	onStart,
	isStarting,
}: {
	course: CoursePublicDetail;
	onStart: () => void;
	isStarting: boolean;
}): React.ReactElement {
	const { t } = useTranslation("course-public");
	const state = resolveEnrollState(course);

	return (
		<MobileActionBar
			action={
				<Button
					className="cursor-pointer pointer-coarse:h-11 disabled:cursor-not-allowed"
					loading={isStarting}
					disabled={state === "closed"}
					onClick={onStart}
				>
					{state === "enrolled" ? null : <IconPlayerPlay size={16} stroke={1.9} aria-hidden />}
					{t(ENROLL_CTA_LABEL_KEY[state])}
				</Button>
			}
		>
			<PriceBadge pricing={course.pricing} />
		</MobileActionBar>
	);
}

/** Мета-теги и микроразметка витрины — всё, что читает робот, в одном месте. */
function CoursePublicMeta({ course }: { course: CoursePublicDetail }): React.ReactElement {
	const { t } = useTranslation("courses");
	// Адрес именно ЭТОЙ языковой версии: на `/de/catalog/{slug}` микроразметка
	// обязана указывать на немецкую страницу, иначе робот получает от неё же
	// заявку «настоящая версия — английская». Значение живое: у вошедшего
	// префикс уходит из адреса, и разметка обязана уйти вместе с ним.
	const addressLocale = useAddressLocale();
	const url = `${SITE_URL}${withLocalePrefix(`/catalog/${course.slug}`, addressLocale)}`;
	const catalogUrl = `${SITE_URL}${withLocalePrefix("/catalog", addressLocale)}`;

	const courseSchema = useMemo(
		() => ({
			"@type": "Course",
			name: course.title,
			description: course.listing.summary ?? course.description ?? course.title,
			url,
			inLanguage: course.locale,
			...(course.cover.url ? { image: course.cover.url } : {}),
			provider: {
				"@type": "Organization",
				name:
					course.author.display === "user" && course.author.name ? course.author.name : "StudyHub",
				url: SITE_URL,
			},
			offers: {
				"@type": "Offer",
				price:
					course.pricing.kind === "paid" && course.pricing.priceMinor !== null
						? (course.pricing.priceMinor / 100).toFixed(2)
						: "0",
				priceCurrency: course.pricing.currency,
				availability: "https://schema.org/InStock",
				url,
			},
		}),
		[course, url],
	);

	const breadcrumbSchema = useMemo(
		() => ({
			"@type": "BreadcrumbList",
			itemListElement: [
				{ "@type": "ListItem", position: 1, name: "StudyHub", item: SITE_URL },
				{
					"@type": "ListItem",
					position: 2,
					name: t("catalog.title"),
					item: catalogUrl,
				},
				{ "@type": "ListItem", position: 3, name: course.title, item: url },
			],
		}),
		[course.title, url, catalogUrl, t],
	);

	return (
		<>
			<PageMeta
				title={course.title}
				description={course.listing.summary ?? course.description ?? undefined}
				path={`/catalog/${course.slug}`}
				locale={course.locale}
				og={{
					type: "article",
					...(course.cover.url ? { image: course.cover.url } : {}),
					imageAlt: course.title,
				}}
			/>
			<JsonLd id="course" schema={courseSchema} />
			<JsonLd id="course-breadcrumb" schema={breadcrumbSchema} />
			{/* `FAQPage` отдаёт сам `FaqAccordion` внутри секции FAQ: разметка
			    обязана описывать ровно то, что показано на экране, и держать её
			    рядом с рендером — единственный способ не разъехаться. Вторая
			    копия здесь давала бы на странице два блока FAQPage. */}
		</>
	);
}

interface CoursePublicBodyProps {
	online: boolean;
	isLoading: boolean;
	isError: boolean;
	notFound: boolean;
	course: CoursePublicDetail | null;
	onRetry: () => void;
	onStart: () => void;
	isStarting: boolean;
}

function CoursePublicBody({
	online,
	isLoading,
	isError,
	notFound,
	course,
	onRetry,
	onStart,
	isStarting,
}: CoursePublicBodyProps): React.ReactElement {
	const { t } = useTranslation("courses");

	if (isLoading) return <CoursePublicSkeleton />;

	// Офлайн раньше ошибки: витрина не хранится на устройстве.
	if (!online && course === null) {
		return (
			<OfflineDetailState
				title={t("catalog.offlineTitle")}
				description={t("catalog.offlineHint")}
				onRetry={onRetry}
			/>
		);
	}

	if (isError || course === null) {
		return (
			<ErrorState
				title={notFound ? t("catalog.public.notFoundTitle") : t("catalog.errorTitle")}
				description={notFound ? t("catalog.public.notFoundHint") : t("catalog.errorHint")}
				action={
					notFound ? (
						<Button asChild variant="outline" className="cursor-pointer">
							<Link to="/catalog" preload="intent">
								{t("catalog.public.backToCatalog")}
							</Link>
						</Button>
					) : (
						<Button variant="outline" className="cursor-pointer" onClick={onRetry}>
							{t("catalog.retry")}
						</Button>
					)
				}
			/>
		);
	}

	return (
		<CourseLanding course={course} surface="public" onStart={onStart} isStarting={isStarting} />
	);
}
